import type { ReactNode } from "react";

import { classes } from "./classes.js";
import { RAIL_FOOT_BAR, RAIL_NAV } from "./rail.js";
import {
  ACCENT_DOUX,
  ALERTE_PASTILLE,
  ACCENT_TEXTE,
  FOND_SURFACE,
  METADONNEE,
  SURFACE_BORDER,
  TEXTE_SECONDAIRE,
} from "./tokens.js";

/**
 * `P1-UIK-027`: **the rail three of the four mockups draw, drawn once.**
 *
 * `hakili-bank.jsx`, `hakili-console.jsx` and `hakili-dealer.jsx` each declare
 * their own `NavRail`, and the three are the same component with different
 * lists: `w-20` fixed, white, a hairline on the right, the mark at the top over
 * the app's own word, then one item per screen , icon above label , with the
 * active one wearing `bg-indigo-50 text-indigo-600`.
 *
 * **Three copies is how the console came to show `Dossiers` twice** while the
 * profile screen's own list of the same views showed it once (round 179's
 * finding, recorded in `app-shell`'s `navigation`). The lists must differ; the
 * rail must not.
 *
 * ## What it refuses to know
 *
 * 4.6.2: *"Zero business logic and zero API calls. A component that knows what
 * a dossier is belongs in an app, not here."* So:
 *
 * - **the items are a prop**, never derived , the app computes them from the
 *   role's whitelist through `navigation()`, which is the ONE place allowed to
 *   answer *which screens may this person see*;
 * - **the active item is a prop**, never inferred from `location` , a rail
 *   that read the URL would be a second router;
 * - **the icon is a `ReactNode`**, never an icon name. The mockups use `lucide`
 *   and this package takes no runtime dependency to draw a rail (166 §1.4:
 *   *"no new runtime dependency without naming it; prefer zero"*). The app
 *   passes the element it already imports.
 *
 * `activeOn` lives in the app's own table because a DETAIL route belongs to its
 * parent screen , nobody navigates to `/dossiers/:id` from a rail , and that
 * mapping is `app-shell`'s, not this component's.
 */

export interface NavRailItem {
  /** The stable screen id, which is also the React key. */
  readonly screen: string;
  /** The word under the icon, from the catalogue. This package holds no French. */
  readonly label: string;
  /**
   * `DEALER-PREQUAL` (round A-D1): **the entry's NAME where the word under the icon is shorter than it** (the rail's one-line ceiling keeps « Estimer », the entry is « Estimer un financement »). Absent, the
   * label is the name. Optional and additive: no other app sets it.
   */
  readonly accessibleName?: string;
  /** Where the item goes. */
  readonly path: string;
  /** Drawn as given: this package does not know what a `FolderOpen` is. */
  readonly icon?: ReactNode;
  /**
   * The selector a spec and a walk reach for, `round F`.
   *
   * **`P0-UIK-033`'s lesson, applied before it costs a second day.** `Button`
   * dropped every `data-testid` it was handed because `ButtonProps` never
   * declared one, and eight controls across the apps were unselectable without
   * anybody being told. A rail whose entries cannot be named is a rail whose
   * `P0-DLW-007` control , *the employee is not offered « Équipe »* , can only
   * be written against a class name or a position.
   */
  readonly testId?: string;
  /**
   * `PRICE-GAP-DEALER`, M3: **a red dot on the entry while something there waits for the person's hand.** A fact the
   * app computed (this package knows nothing of what is pending), drawn with the bell's own token. Absent or false
   * draws nothing; it clears when the app stops passing it.
   */
  readonly pastille?: boolean;
  /** The dot's accessible name, from the catalogue (this package holds no French). */
  readonly pastilleLabel?: string;
}

export interface NavRailProps {
  readonly items: readonly NavRailItem[];
  /** The screen currently rendered, resolved once by the shell. */
  readonly actif: string;
  /** The product mark, drawn by the app. */
  readonly mark: ReactNode;
  /**
   * The app's own word under the mark: `bank`, `console`, `concession`.
   *
   * **Optional since `round F2`, and the dealer passes none.** The founder's
   * note of 7 September, on the pair: the drawing carries the mark ALONE. The
   * word was this component's invention , no mockup draws it , and a rail is
   * eighty pixels wide, where every line that is not an entry is a line taken
   * from the entries.
   */
  readonly appName?: string | undefined;
  /** Where the mark leads, which is the persona's landing screen. */
  readonly accueilPath: string;
  /** In-app navigation. A rail that reloaded would drop the in-memory token. */
  readonly naviguer: (to: string) => void;
  /** The stub convention's entry point, when the app offers one. */
  readonly footer?: ReactNode;
}

export function NavRail({
  items,
  actif,
  mark,
  appName,
  accueilPath,
  naviguer,
  footer,
}: NavRailProps): React.JSX.Element {
  return (
    <aside
      data-kit="rail"
      className={classes(
        "sticky top-0 z-30 flex h-screen w-24 shrink-0 flex-col items-center overflow-hidden border-r pb-5 pt-3",
        SURFACE_BORDER,
        FOND_SURFACE,
      )}
    >
      <a
        href={accueilPath}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          naviguer(accueilPath);
        }}
        className="mb-1 shrink-0 no-underline"
      >
        {mark}
      </a>
      {appName === undefined ? null : (
        <span className={classes("mb-3 shrink-0 font-medium", METADONNEE, TEXTE_SECONDAIRE)}>{appName}</span>
      )}

      <nav className={RAIL_NAV}>
        {items.map((item) => {
          const active = item.screen === actif;
          return (
            <a
              key={item.screen}
              href={item.path}
              // Real anchors, and only the plain left click is intercepted:
              // middle-click and "open in new tab" keep working, and a reload
              // is what a rail must never cause.
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                naviguer(item.path);
              }}
              aria-current={active ? "page" : undefined}
              // The label is hidden under 1 100 px in the bank, so the name is stated, always (D3 4.3).
              aria-label={item.accessibleName ?? item.label}
              data-kit="rail-item"
              data-testid={item.testId}
              className={classes(
                // `text-center` and `no-underline`: the drawing's labels are
                // centred under their icon and carry no underline, and an
                // anchor's default is the opposite of both. `leading-tight` is
                // what lets « Tableau de bord » sit on two lines in eighty
                // pixels without the entries drifting apart.
                "flex w-full shrink-0 flex-col items-center gap-0.5 px-1 py-1.5 text-center leading-tight no-underline transition-colors",
                METADONNEE,
                active ? classes(ACCENT_DOUX, ACCENT_TEXTE) : TEXTE_SECONDAIRE,
              )}
            >
              <span className="relative inline-flex">
                {item.icon}
                {item.pastille === true ? (
                  <span
                    role="img"
                    aria-label={item.pastilleLabel}
                    data-testid={item.testId === undefined ? undefined : `${item.testId}-pastille`}
                    className={classes("absolute -right-1 -top-0.5 h-2 w-2 rounded-full", ALERTE_PASTILLE)}
                  />
                ) : null}
              </span>
              {item.label}
            </a>
          );
        })}
      </nav>

      {footer === undefined ? null : <div className={RAIL_FOOT_BAR}>{footer}</div>}
    </aside>
  );
}
